import { useState } from 'react';
import { Link } from 'react-router-dom';
import { pricingPlans } from '@/mocks/homeData';

export default function PricingSection() {
  const [billingInterval, setBillingInterval] = useState<'oneTime' | 'installment'>('oneTime');

  return (
    <section id="pricing" className="py-20 md:py-28 px-4 md:px-6">
      <div className="mx-auto max-w-7xl">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-4">
            选择适合你的学习方案
          </h2>
          <p className="text-sm text-foreground-600 max-w-lg mx-auto">
            下单前请仔细确认课程详情，购买后默认不予退款。特殊情况请参阅退款政策。
          </p>

          <div className="inline-flex items-center bg-background-100 rounded-full p-1 mt-8">
            <button
              onClick={() => setBillingInterval('oneTime')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                billingInterval === 'oneTime'
                  ? 'bg-background-50 text-foreground-950'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              一次性付款
            </button>
            <button
              onClick={() => setBillingInterval('installment')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                billingInterval === 'installment'
                  ? 'bg-background-50 text-foreground-950'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              分期付款
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-start">
          {pricingPlans.map((plan) => {
            const isMonthly = billingInterval === 'installment';
            const displayPrice = isMonthly ? Math.round(plan.price / 3) : plan.price;
            const displayOriginalPrice = isMonthly ? Math.round(plan.originalPrice / 3) : plan.originalPrice;

            return (
              <div
                key={plan.name}
                className={`relative rounded-2xl p-6 md:p-8 transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-foreground-900 scale-[1.02] md:scale-105'
                    : 'bg-background-100 hover:bg-background-200/60'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-accent-500 text-foreground-950 text-xs font-semibold rounded-full whitespace-nowrap">
                    最受欢迎
                  </div>
                )}

                <h3 className={`font-heading text-xl mb-1 ${plan.highlighted ? 'text-background-50' : 'text-foreground-950'}`}>
                  {plan.name}
                </h3>
                <p className={`text-xs mb-6 ${plan.highlighted ? 'text-background-300' : 'text-foreground-500'}`}>
                  {isMonthly ? '每月支付，共3期' : '一次性支付'}
                </p>

                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className={`text-sm ${plan.highlighted ? 'text-background-300' : 'text-foreground-500'}`}>¥</span>
                    <span className={`font-heading text-4xl md:text-5xl ${plan.highlighted ? 'text-primary-400' : 'text-primary-500'}`}>
                      {displayPrice.toLocaleString()}
                    </span>
                    {isMonthly && (
                      <span className={`text-sm ${plan.highlighted ? 'text-background-300' : 'text-foreground-500'}`}>/月</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs line-through ${plan.highlighted ? 'text-background-400' : 'text-foreground-400'}`}>
                      ¥{displayOriginalPrice.toLocaleString()}
                    </span>
                    <span className="text-xs text-accent-500 font-medium">
                      省 ¥{(displayOriginalPrice - displayPrice).toLocaleString()}
                    </span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2.5">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className={`ri-check-line text-sm ${plan.highlighted ? 'text-accent-500' : 'text-primary-500'}`}></i>
                      </div>
                      <span className={`text-sm ${plan.highlighted ? 'text-background-200' : 'text-foreground-700'}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/checkout"
                  className={`block w-full text-center py-3 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    plan.highlighted
                      ? 'bg-accent-500 text-foreground-950 hover:bg-accent-600'
                      : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  }`}
                >
                  立即报名
                </Link>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-foreground-400 mt-8">
          所有价格均包含税费 · 支持支付宝 / 微信支付 / 银行转账
        </p>
      </div>
    </section>
  );
}